import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { LotterySelector } from "@/components/app/LotterySelector";
import { useLottery, LOTTERIES } from "@/hooks/use-lottery-selection";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  POOL_LIMITS,
  ticketPrice,
  generateComplete,
  generateStrategic,
  previewStrategic,
  generatePairs,
  generateFixed,
  estimatePairs,
  estimateFixed,
  getPairsAnchor,
  combinations,
  type LotterySlug,
} from "@/lib/pool-math";
import { STRATEGIC, strategicVHint, strategicTHint } from "@/lib/strategic-matrix";
import {
  generateSmart,
  computeSumRange,
  DEFAULT_FILTERS,
  type SmartFilters,
  type NumberStat,
} from "@/lib/smart-generator";
import { cn, formatInt, formatBRL } from "@/lib/utils";
import { Loader2, Sparkles, Lock, Pin, AlertCircle, Wand2, Users, Bot } from "lucide-react";
import { Link } from "react-router-dom";
import { usePlanLimits } from "@/hooks/use-plan-limits";
import { PairBuilder } from "@/components/app/PairBuilder";
import {
  formatSpecialDate,
  getNextContestNumber,
} from "@/lib/check-scope";
import { AiPoolDialog, type AiPoolFormValues } from "@/components/app/boloes/AiPoolDialog";
import { useNumberStats, statTitle } from "@/hooks/use-number-stats";
import { Trash2 } from "lucide-react";

import {
  PoolGameBasket,
  usePoolBasket,
  basketCost,
  type GameSource,
} from "@/components/app/boloes/PoolGameBasket";

type Mode = "manual" | "complete" | "closure" | "pairs" | "fixed" | "smart" | "ai";

const SMART_FILTER_KEYS: Array<keyof SmartFilters> = [
  "parityBalance",
  "sumInRange",
  "decadeSpread",
  "noLongRuns",
  "preferHot",
  "includeOverdue",
  "avoidPastDraws",
];

export function PoolWizard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selected, config } = useLottery();
  const slug = selected as LotterySlug;
  const limits = POOL_LIMITS[slug];
  const strategic = STRATEGIC[slug];
  const qc = useQueryClient();
  const { canAddPool, limits: planLimits, plan, refreshUsage } = usePlanLimits();
  const closureAllowed = planLimits?.closure_generator ?? false;
  const poolAvailability = canAddPool();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  // Hidratação inicial via localStorage (mantém dezenas escolhidas ao navegar
  // e sobrevive a recarregar a página ou abrir em outra aba).
  const readInitialPicks = () => {
    if (typeof window === "undefined") return { selected: [] as number[], fixed: [] as number[], pairs: [] as [number, number][] };
    try {
      const raw = localStorage.getItem(`lt-bolao-picks-${slug}`);
      if (!raw) return { selected: [], fixed: [], pairs: [] };
      const p = JSON.parse(raw);
      return {
        selected: Array.isArray(p.selected) ? p.selected : [],
        fixed: Array.isArray(p.fixed) ? p.fixed : [],
        pairs: Array.isArray(p.pairs) ? p.pairs : [],
      };
    } catch {
      return { selected: [], fixed: [], pairs: [] };
    }
  };
  const __initialPicks = readInitialPicks();
  const [selectedNumbers, setSelectedNumbers] = useState<number[]>(__initialPicks.selected);
  const [fixedNumbers, setFixedNumbers] = useState<number[]>(__initialPicks.fixed);
  const [customPairs, setCustomPairs] = useState<[number, number][]>(__initialPicks.pairs);
  const [mode, setMode] = useState<Mode>("complete");
  const [k, setK] = useState<number>(limits.baseNumbers);
  const [guaranteeHits, setGuaranteeHits] = useState<number>(strategic.tOptions[0]);
  const [strategicInfo, setStrategicInfo] = useState<{
    target: number;
    actual: number;
    badge: "melhor" | "exato" | "aproximacao";
    coverageVerified?: boolean;
    coverageTested?: number;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [smartQty, setSmartQty] = useState<number>(10);
  const [smartFilters, setSmartFilters] = useState<SmartFilters>(DEFAULT_FILTERS);
  const [aiQty, setAiQty] = useState<number>(5);
  const [aiHint, setAiHint] = useState<string>("");
  const isPremium = plan === "premium";
  const [contestNumber, setContestNumber] = useState<number | null>(null);
  const [nextContest, setNextContest] = useState<number | null>(null);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiPending, setAiPending] = useState(false);
  const basket = usePoolBasket(slug);

  const scopeRangeFor = () => ({ from: contestNumber, until: contestNumber });

  const { data: numberStats } = useNumberStats(slug);

  // Persiste picks por loteria.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(
        `lt-bolao-picks-${slug}`,
        JSON.stringify({ selected: selectedNumbers, fixed: fixedNumbers, pairs: customPairs }),
      );
    } catch { /* ignore */ }
  }, [selectedNumbers, fixedNumbers, customPairs, slug]);

  const clearPicks = () => {
    setSelectedNumbers([]);
    setFixedNumbers([]);
    setCustomPairs([]);
    if (typeof window !== "undefined") {
      try { localStorage.removeItem(`lt-bolao-picks-${slug}`); } catch { /* ignore */ }
    }
  };

  useEffect(() => {
    let cancelled = false;
    setContestNumber(null);
    setNextContest(null);
    getNextContestNumber(slug).then((n) => { if (!cancelled) setNextContest(n); });
    return () => { cancelled = true; };
  }, [slug]);

  const { data: scheduledContests = [] } = useQuery({
    queryKey: ["pool-scheduled-contests", slug, nextContest],
    enabled: nextContest != null,
    queryFn: async () => {
      const { data, error } = await supabase.from("lottery_schedule")
        .select("contest_number, draw_date, is_special, special_name, id, lotteries!inner(slug)")
        .eq("lotteries.slug", slug).gte("contest_number", nextContest ?? 1)
        .order("contest_number").limit(20);
      if (error) throw error;
      return (data ?? []).filter((item) => item.contest_number != null);
    },
  });
  const contestOptions = scheduledContests.some((item) => item.contest_number === nextContest)
    ? scheduledContests
    : nextContest != null ? [{ contest_number: nextContest, draw_date: null, is_special: false, special_name: null, id: null }, ...scheduledContests] : scheduledContests;
  const selectedSchedule = scheduledContests.find((item) => item.contest_number === contestNumber);

  const { data: lotteryRow } = useQuery({
    queryKey: ["lottery-id", slug],
    queryFn: async () => {
      const { data } = await supabase.from("lotteries").select("id").eq("slug", slug).maybeSingle();
      return data;
    },
  });

  // Dados para o modo "smart" (estatísticas + sorteios passados)
  const { data: smartData } = useQuery({
    queryKey: ["pool-smart-data", slug],
    enabled: mode === "smart" && !!lotteryRow,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const [statsRes, drawsRes] = await Promise.all([
        supabase.rpc("get_number_stats", { _lottery_slug: slug }),
        supabase
          .from("draws")
          .select("id, draw_numbers(number)")
          .eq("lottery_id", lotteryRow!.id)
          .order("contest_number", { ascending: false })
          .limit(1000),
      ]);
      const stats: NumberStat[] = (statsRes.data ?? []).map((s: any) => ({
        number: s.number,
        frequency: Number(s.frequency),
        delay: s.delay,
      }));
      const pastDraws = (drawsRes.data ?? [])
        .map((d: any) => (d.draw_numbers ?? []).map((dn: any) => dn.number).sort((a: number, b: number) => a - b))
        .filter((arr: number[]) => arr.length > 0);
      return { stats, pastDraws, sumRange: computeSumRange(pastDraws) };
    },
  });

  // Ao trocar de loteria, restaura picks salvos daquela loteria.
  const resetForLottery = () => {
    if (typeof window !== "undefined") {
      try {
        const raw = localStorage.getItem(`lt-bolao-picks-${slug}`);
        const p = raw ? JSON.parse(raw) : {};
        setSelectedNumbers(Array.isArray(p.selected) ? p.selected : []);
        setFixedNumbers(Array.isArray(p.fixed) ? p.fixed : []);
        setCustomPairs(Array.isArray(p.pairs) ? p.pairs : []);
      } catch {
        setSelectedNumbers([]);
        setFixedNumbers([]);
        setCustomPairs([]);
      }
    }
    setK(limits.baseNumbers);
    setGuaranteeHits(STRATEGIC[slug].tOptions[0]);
  };

  // Quando entra em Fechamento Estratégico, força k e t para os valores
  // da matriz curada (por loteria).
  useEffect(() => {
    if (mode === "closure") {
      setK(strategic.k);
      if (!strategic.tOptions.includes(guaranteeHits)) {
        setGuaranteeHits(strategic.tOptions[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, slug]);

  const strategicVOk = mode !== "closure" || strategic.vOptions.includes(selectedNumbers.length);
  const strategicMaxSelect = mode === "closure"
    ? strategic.vOptions[strategic.vOptions.length - 1]
    : limits.maxSelected;

  // Geração via dialog dedicado (igual ao Gerador Inteligente).
  // Cria o bolão diretamente a partir dos valores do formulário, sem
  // depender do state da página (que pode estar fora de sincronia).
  const handleAiDialogConfirm = async (vals: AiPoolFormValues) => {
    if (contestNumber == null || contestNumber < (nextContest ?? Infinity) || !contestOptions.some((item) => item.contest_number === contestNumber)) {
      toast.error("Escolha o concurso dos jogos antes de criar o bolão");
      return;
    }
    if (!user || !lotteryRow) {
      toast.error("Faça login para gerar bolões");
      return;
    }
    if (!isPremium) {
      toast.error("Gerador IA é exclusivo do plano Premium.");
      return;
    }
    if (!poolAvailability.ok) {
      toast.error(
        `Limite de bolões ativos atingido no plano ${plan.toUpperCase()}. Faça upgrade para criar mais.`,
      );
      return;
    }
    const kAi = Math.max(limits.baseNumbers, Math.min(limits.maxPickPerGame, vals.numbersPerGame));
    const qtyAi = Math.max(1, Math.min(10, vals.gameCount));

    setAiPending(true);
    try {
      const { data, error } = await supabase.functions.invoke("ai-generate-games", {
        body: {
          lotterySlug: slug,
          numbersPerGame: kAi,
          gameCount: qtyAi,
          hint: vals.hint || undefined,
        },
      });
      if (error) throw new Error(error.message || "Erro ao gerar com IA");
      if ((data as { error?: string })?.error) {
        throw new Error((data as { error: string }).error);
      }
      const aiGames =
        (data as { games?: { numbers: number[] }[] })?.games ?? [];
      const games = aiGames.map((g) => g.numbers);
      if (games.length === 0) {
        toast.error("A IA não retornou jogos. Tente novamente.");
        return;
      }

      const union = new Set<number>();
      games.forEach((g) => g.forEach((n) => union.add(n)));
      const storedSelected = [...union].sort((a, b) => a - b);
      const costPerGame = ticketPrice(slug, kAi);
      const totalCost = costPerGame * games.length;
      const scopeRange = scopeRangeFor();


      const { data: pool, error: poolErr } = await supabase
        .from("pools")
        .insert({
          user_id: user.id,
          lottery_id: lotteryRow.id,
          name: vals.name,
          description: vals.description || null,
          selected_numbers: storedSelected,
          generation_mode: "ai",
          numbers_per_game: kAi,
          guarantee_hits: null,
          guarantee_if: null,
          total_games: games.length,
          cost_per_game: costPerGame,
          total_cost: totalCost,
          status: "active",
          check_scope: selectedSchedule?.is_special ? "special" : "next_only",
          check_from_contest: scopeRange.from,
          check_until_contest: scopeRange.until,
          special_schedule_id: selectedSchedule?.is_special ? selectedSchedule.id : null,

        })
        .select()
        .single();
      if (poolErr || !pool) throw poolErr ?? new Error("Falha ao criar bolão");

      const rows = games.map((numbers, i) => ({
        pool_id: pool.id,
        user_id: user.id,
        position: i + 1,
        numbers,
      }));
      const { error: gamesErr } = await supabase.from("pool_games").insert(rows);
      if (gamesErr) throw gamesErr;

      toast.success(`Bolão criado com ${games.length} jogos pela IA`);
      qc.invalidateQueries({ queryKey: ["pools"] });
      refreshUsage();
      setAiDialogOpen(false);
      navigate(`/app/boloes/${pool.id}`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Erro desconhecido";
      toast.error(`Erro ao gerar bolão com IA: ${msg}`);
    } finally {
      setAiPending(false);
    }
  };

  const universe = useMemo(
    () => Array.from({ length: config.max - config.min + 1 }, (_, i) => config.min + i),
    [config],
  );

  // Orientação da faixa de dezenas para "Combinação por duplas".
  const pairsHint =
    slug === "lotofacil"
      ? "Lotofácil: selecione um número ímpar de dezenas entre 17 e 25 (jogos de 15 = âncora + 7 duplas)."
      : slug === "quina"
        ? "Quina: selecione um número ímpar de dezenas entre 7 e 39 (jogos de 5 = âncora + 2 duplas)."
        : "Selecione uma quantidade par de dezenas (jogos de 6 = 3 duplas).";

  const toggleNumber = (n: number) => {
    setSelectedNumbers((prev) => {
      if (prev.includes(n)) return prev.filter((x) => x !== n);
      const cap =
        mode === "closure"
          ? strategicMaxSelect
          : mode === "manual"
          ? limits.maxPickPerGame
          : limits.maxSelected;
      if (prev.length >= cap) {
        toast.error(
          mode === "closure"
            ? `Para Fechamento Estratégico de ${config.name}, escolha ${strategicVHint(slug)} dezenas.`
            : mode === "manual"
            ? `Um jogo de ${config.name} tem no máximo ${limits.maxPickPerGame} dezenas`
            : `Máximo de ${limits.maxSelected} dezenas para ${config.name}`,
        );
        return prev;
      }
      return [...prev, n].sort((a, b) => a - b);
    });
    // Se desmarcou uma dezena que estava como fixa, retira da lista de fixas
    setFixedNumbers((prev) => prev.filter((x) => x !== n || selectedNumbers.includes(n)));
  };

  const toggleFixed = (n: number) => {
    if (!selectedNumbers.includes(n)) return;
    setFixedNumbers((prev) =>
      prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n].sort((a, b) => a - b),
    );
  };

  const preview = useMemo(() => {
    if (mode === "manual") return null;
    if (mode === "smart") {
      const total = Math.max(1, Math.min(1000, smartQty));
      const realCost = total * ticketPrice(slug, k);
      return { total, cost: realCost, isUpper: false, isStrategic: false };
    }
    if (mode === "ai") {
      const total = Math.max(1, Math.min(10, aiQty));
      return { total, cost: total * ticketPrice(slug, k), isUpper: false, isStrategic: false };
    }
    const minSel = mode === "closure" ? strategic.vOptions[0] : limits.minSelected;
    if (selectedNumbers.length < minSel) return null;
    let total = 0;
    let isStrategic = false;
    if (mode === "complete") {
      total = combinations(selectedNumbers.length, k);
    } else if (mode === "pairs") {
      if (selectedNumbers.length % 2 !== k % 2) return null;
      total = estimatePairs(selectedNumbers.length, k);
    } else if (mode === "fixed") {
      const variables = selectedNumbers.length - fixedNumbers.length;
      total = estimateFixed(fixedNumbers.length, variables, k);
    } else {
      // closure: previsão = target da matriz curada.
      const tgt = previewStrategic(slug, selectedNumbers.length, guaranteeHits);
      if (tgt === null) return null;
      total = tgt;
      isStrategic = true;
    }
    if (!total) return null;
    const realCost = total * ticketPrice(slug, k);
    return { total, cost: realCost, isUpper: false, isStrategic };
  }, [selectedNumbers, fixedNumbers, k, mode, slug, limits, smartQty, guaranteeHits, strategic]);

  /**
   * Gera os jogos conforme a estratégia atual, validando os parâmetros.
   * Retorna `null` quando algo impede a geração (erro já exibido em toast).
   */
  const buildGames = async (): Promise<number[][] | null> => {
    if (mode === "closure" && !closureAllowed) {
      toast.error("Fechamento estratégico é exclusivo dos planos Pro e Premium.");
      return null;
    }
    if (mode === "ai" && !isPremium) {
      toast.error("Gerador IA é exclusivo do plano Premium.");
      return null;
    }

    if (mode === "manual") {
      if (selectedNumbers.length < limits.baseNumbers) {
        toast.error(`Escolha ao menos ${limits.baseNumbers} dezenas para montar um jogo`);
        return null;
      }
      if (selectedNumbers.length > limits.maxPickPerGame) {
        toast.error(`${config.name} aceita no máximo ${limits.maxPickPerGame} dezenas por jogo`);
        return null;
      }
      return [[...selectedNumbers].sort((a, b) => a - b)];
    }

    if (mode !== "smart" && mode !== "ai" && selectedNumbers.length < limits.minSelected) {
      toast.error(`Selecione ao menos ${limits.minSelected} dezenas`);
      return null;
    }
    if (mode !== "smart" && mode !== "ai" && (k < limits.baseNumbers || k > selectedNumbers.length)) {
      toast.error(`Dezenas por jogo deve estar entre ${limits.baseNumbers} e ${selectedNumbers.length}`);
      return null;
    }
    if (mode !== "smart" && mode !== "ai" && k > limits.maxPickPerGame) {
      toast.error(`${config.name} aceita no máximo ${limits.maxPickPerGame} dezenas por jogo`);
      return null;
    }
    if ((mode === "smart" || mode === "ai") && (k < limits.baseNumbers || k > limits.maxPickPerGame)) {
      toast.error(`Dezenas por jogo deve estar entre ${limits.baseNumbers} e ${limits.maxPickPerGame}`);
      return null;
    }

    // Guarda específica para fechamento estratégico (set-cover é exponencial)
    if (mode === "closure") {
      const upper = combinations(selectedNumbers.length, k);
      if (upper > 50000) {
        toast.error(
          `Fechamento estratégico inviável: ${formatInt(upper)} combinações base. Reduza dezenas, aumente a meta ou use "Combinações completas" / "Por duplas".`,
        );
        return null;
      }
    }
    // Confirmação para volumes altos nos demais modos
    if (mode === "complete" || mode === "pairs" || mode === "fixed") {
      let estimate = 0;
      if (mode === "complete") estimate = combinations(selectedNumbers.length, k);
      else if (mode === "pairs") estimate = estimatePairs(selectedNumbers.length, k);
      else estimate = estimateFixed(fixedNumbers.length, selectedNumbers.length - fixedNumbers.length, k);
      if (estimate > 1000) {
        const cost = estimate * ticketPrice(slug, k);
        const ok = window.confirm(
          `Você está prestes a gerar ${formatInt(estimate)} jogos.\n\n` +
          `Custo total estimado: ${formatBRL(cost)}.\n\n` +
          `Volumes muito altos podem demorar alguns segundos para gerar.\n\n` +
          `Deseja continuar? Cancele para escolher outra estratégia (fechamento estratégico ou por duplas).`,
        );
        if (!ok) return null;
      }
    }

    let games: number[][] = [];
    if (mode === "complete") {
      games = generateComplete(selectedNumbers, k);
    } else if (mode === "closure") {
      if (!strategicVOk) {
        toast.error(
          `Para Fechamento Estratégico de ${config.name}, escolha exatamente ${strategicVHint(slug)} dezenas.`,
        );
        return null;
      }
      const r = generateStrategic(slug, selectedNumbers, guaranteeHits);
      games = r.games;
      setStrategicInfo({
        target: r.target,
        actual: r.actual,
        badge: r.badge,
        coverageVerified: r.coverageVerified,
        coverageTested: r.coverageTested,
      });
    } else if (mode === "pairs") {
      if (selectedNumbers.length % 2 !== k % 2) {
        toast.error(
          selectedNumbers.length % 2 !== 0
            ? "Seleção ímpar: escolha uma quantidade ímpar de dezenas por jogo"
            : "Seleção par: escolha uma quantidade par de dezenas por jogo",
        );
        return null;
      }
      const usePairs =
        customPairs.length === Math.floor(selectedNumbers.length / 2) && customPairs.length > 0
          ? customPairs
          : null;
      games = generatePairs(selectedNumbers, k, usePairs);
    } else if (mode === "fixed") {
      if (fixedNumbers.length === 0) {
        toast.error("Marque ao menos uma dezena como fixa");
        return null;
      }
      if (fixedNumbers.length >= k) {
        toast.error(`Fixas (${fixedNumbers.length}) devem ser menos que ${k} dezenas/jogo`);
        return null;
      }
      const variables = selectedNumbers.filter((n) => !fixedNumbers.includes(n));
      games = generateFixed(fixedNumbers, variables, k);
    } else if (mode === "smart") {
      if (!smartData) {
        toast.error("Carregando dados estatísticos — aguarde alguns segundos");
        return null;
      }
      const result = generateSmart({
        min: config.min,
        max: config.max,
        pick: k,
        filters: smartFilters,
        stats: smartData.stats,
        pastDraws: smartData.pastDraws,
        sumRange: smartData.sumRange,
        count: Math.max(1, Math.min(1000, smartQty)),
      });
      games = result.games;
      if (games.length < smartQty) {
        toast.warning(`Gerados ${games.length} de ${smartQty} jogos (filtros restritivos)`);
      }
    } else if (mode === "ai") {
      const { data, error } = await supabase.functions.invoke("ai-generate-games", {
        body: {
          lotterySlug: slug,
          numbersPerGame: k,
          gameCount: Math.max(1, Math.min(10, aiQty)),
          hint: aiHint || undefined,
        },
      });
      if (error) {
        toast.error(error.message || "Erro ao gerar com IA");
        return null;
      }
      if ((data as { error?: string })?.error) {
        toast.error((data as { error: string }).error);
        return null;
      }
      const aiGames = (data as { games?: { numbers: number[] }[] })?.games ?? [];
      games = aiGames.map((g) => g.numbers);
    }

    if (games.length === 0) {
      toast.error("Nenhum jogo gerado — ajuste os parâmetros");
      return null;
    }
    return games;
  };

  /** Adiciona os jogos gerados (ou o jogo montado à mão) à lista do bolão. */
  const handleAddToBasket = async () => {
    setSaving(true);
    try {
      const games = await buildGames();
      if (!games) return;
      const anchor = mode === "pairs" ? getPairsAnchor(selectedNumbers, customPairs) : undefined;
      const { added, duplicates } = basket.add(games, mode as GameSource, anchor ?? undefined);
      if (added > 0) {
        toast.success(
          `${added} jogo(s) adicionado(s) à lista${duplicates > 0 ? ` • ${duplicates} duplicado(s) ignorado(s)` : ""}`,
        );
      } else {
        toast.warning("Todos os jogos já estavam na lista (duplicados)");
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Erro desconhecido";
      toast.error(`Erro ao gerar jogos: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  /** Cria o bolão a partir da lista de jogos montada pelo usuário. */
  const handleCreate = async () => {
    if (!user || !lotteryRow) return;
    if (contestNumber == null || contestNumber < (nextContest ?? Infinity) || !contestOptions.some((item) => item.contest_number === contestNumber)) {
      toast.error("Escolha o concurso dos jogos antes de criar o bolão");
      return;
    }
    if (!poolAvailability.ok) {
      toast.error(`Limite de bolões ativos atingido no plano ${plan.toUpperCase()}. Faça upgrade para criar mais.`);
      return;
    }
    if (!name.trim()) {
      toast.error("Dê um nome ao bolão");
      return;
    }
    if (basket.games.length === 0) {
      toast.error("Adicione ao menos um jogo à lista do bolão");
      return;
    }

    setSaving(true);
    try {
      const games = basket.games.map((g) => g.numbers);
      const sources = [...new Set(basket.games.map((g) => g.source))];
      const generationMode = sources.length === 1 ? sources[0] : "mixed";

      const union = new Set<number>();
      games.forEach((g) => g.forEach((n) => union.add(n)));
      const storedSelected = [...union].sort((a, b) => a - b);

      const totalCost = basketCost(slug, basket.games);
      const sizes = games.map((g) => g.length);
      const mainSize = sizes.slice().sort(
        (a, b) => sizes.filter((s) => s === b).length - sizes.filter((s) => s === a).length,
      )[0];
      const costPerGame = totalCost / games.length;
      const scopeRange = scopeRangeFor();

      const { data: pool, error: poolErr } = await supabase
        .from("pools")
        .insert({
          user_id: user.id,
          lottery_id: lotteryRow.id,
          name: name.trim(),
          description: description.trim() || null,
          selected_numbers: storedSelected,
          generation_mode: generationMode,
          numbers_per_game: mainSize,
          guarantee_hits: generationMode === "closure" ? guaranteeHits : null,
          guarantee_if: generationMode === "closure" ? strategic.k : null,
          total_games: games.length,
          cost_per_game: costPerGame,
          total_cost: totalCost,
          status: "active",
          check_scope: selectedSchedule?.is_special ? "special" : "next_only",
          check_from_contest: scopeRange.from,
          check_until_contest: scopeRange.until,
          special_schedule_id: selectedSchedule?.is_special ? selectedSchedule.id : null,

        })
        .select()
        .single();

      if (poolErr || !pool) throw poolErr ?? new Error("Falha ao criar bolão");

      const rows = games.map((numbers, i) => ({
        pool_id: pool.id,
        user_id: user.id,
        position: i + 1,
        numbers,
      }));

      // Insere em chunks para evitar payload gigante
      const CHUNK = 500;
      for (let i = 0; i < rows.length; i += CHUNK) {
        const { error } = await supabase.from("pool_games").insert(rows.slice(i, i + CHUNK));
        if (error) throw error;
      }

      toast.success(`Bolão criado com ${games.length} jogos`);
      basket.clear();
      qc.invalidateQueries({ queryKey: ["pools"] });
      refreshUsage();
      navigate(`/app/boloes/${pool.id}`);
    } catch (e) {
      console.error("Erro ao criar bolão:", e);
      const msg =
        e instanceof Error
          ? e.message
          : (typeof e === "object" && e !== null && "message" in e)
            ? String((e as { message: unknown }).message)
            : "Erro desconhecido";
      toast.error(`Erro ao criar bolão: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {!poolAvailability.ok && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <Lock className="mt-0.5 h-4 w-4 text-destructive" />
              <div className="text-sm">
                <div className="font-medium text-foreground">Limite de bolões ativos atingido</div>
                <div className="text-muted-foreground">
                  Seu plano <span className="uppercase">{plan}</span> permite até{" "}
                  {planLimits?.active_pools_limit} bolão(ões) ativo(s). Faça upgrade ou conclua um bolão para liberar espaço.
                </div>
              </div>
            </div>
            <Button asChild size="sm">
              <Link to="/planos">Ver planos</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* CTA principal: Gerar com IA (mesma UX do Gerador Inteligente). */}
      <Card className="border-primary/40 bg-primary/5">
        <CardContent className="flex flex-col items-start gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <Bot className="mt-0.5 h-5 w-5 text-primary" />
            <div>
              <div className="font-medium text-foreground">
                Prefere deixar a IA escolher por você?
              </div>
              <p className="text-xs text-muted-foreground">
                A IA Premium analisa o histórico e gera o bolão completo —
                informe os números por jogo, a quantidade e uma instrução opcional.
              </p>
            </div>
          </div>
          <Button
            onClick={() => setAiDialogOpen(true)}
            disabled={!isPremium}
            className="shrink-0 gap-2"
          >
            <Sparkles className="h-4 w-4" />
            {isPremium ? "Gerar com IA" : "Disponível no Premium"}
          </Button>
        </CardContent>
      </Card>

      <AiPoolDialog
        open={aiDialogOpen}
        onOpenChange={setAiDialogOpen}
        onConfirm={handleAiDialogConfirm}
        loading={aiPending}
        minPick={limits.baseNumbers}
        maxPick={limits.maxPickPerGame}
      />

      <Card>
        <CardHeader>
          <CardTitle>Identificação</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <div className="md:col-span-2 space-y-2">
            <Label>Nome do bolão</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Bolão da firma — Mega da Virada" />
          </div>
          <div className="space-y-2">
            <Label>Loteria</Label>
            <div onClick={resetForLottery}><LotterySelector /></div>
          </div>
          <div className="md:col-span-3 space-y-2">
            <Label>Concurso de todos os jogos</Label>
            <Select value={contestNumber != null ? String(contestNumber) : undefined} onValueChange={(v) => setContestNumber(Number(v))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {contestOptions.map((item) => (
                  <SelectItem key={item.contest_number} value={String(item.contest_number)}>
                    Concurso #{item.contest_number}{item.is_special ? ` — ${item.special_name ?? "Especial"}` : ""}
                    {item.draw_date ? ` (${formatSpecialDate(item.draw_date)})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-3 space-y-2">
            <Label>Descrição (opcional)</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="Anotações sobre o bolão" />
          </div>
        </CardContent>
      </Card>

      {mode !== "smart" && mode !== "ai" && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-2">
            <span>
              Dezenas escolhidas{" "}
              <span className="text-sm font-normal text-muted-foreground">
                ({selectedNumbers.length}/{limits.maxSelected} — mínimo {limits.minSelected})
              </span>
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearPicks}
              disabled={selectedNumbers.length === 0}
              className="gap-1.5 text-muted-foreground hover:text-destructive"
              title="Remove todas as dezenas escolhidas — só executa quando você clicar aqui."
            >
              <Trash2 className="h-4 w-4" /> Apagar números escolhidos
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {mode === "fixed" && (
            <div
              className={cn(
                "mb-4 flex items-start gap-3 rounded-lg border-2 p-3 transition-colors",
                fixedNumbers.length === 0
                  ? "border-lt-teal bg-lt-teal/10 animate-pulse"
                  : "border-lt-teal/60 bg-lt-teal/5",
              )}
            >
              <Pin className="mt-0.5 h-5 w-5 shrink-0 fill-lt-teal text-lt-teal" />
              <div className="flex-1 text-[13px]">
                <div className="font-semibold text-foreground">
                  Marque ao menos UMA dezena fixa
                </div>
                <div className="text-muted-foreground">
                  As dezenas fixas aparecem em <strong>todos os jogos gerados</strong>. Clique
                  com o <strong>botão direito</strong> (ou <strong>toque longo</strong>) sobre uma dezena já
                  selecionada — ou use o painel abaixo do quadro.
                </div>
              </div>
              <span
                className={cn(
                  "shrink-0 rounded-full px-3 py-1 text-[12px] font-semibold",
                  fixedNumbers.length === 0
                    ? "bg-lt-teal/20 text-lt-teal"
                    : "bg-lt-teal text-background",
                )}
              >
                {fixedNumbers.length} fixa(s)
              </span>
            </div>
          )}
          <div className="grid grid-cols-10 gap-1.5 sm:grid-cols-12 md:grid-cols-15 lg:grid-cols-20">
            {universe.map((n) => {
              const active = selectedNumbers.includes(n);
              const isFixed = fixedNumbers.includes(n);
              return (
                <button
                  key={n}
                  onClick={() => toggleNumber(n)}
                  onContextMenu={(e) => {
                    if (mode !== "fixed") return;
                    e.preventDefault();
                    toggleFixed(n);
                  }}
                  title={statTitle(n, numberStats?.get(n))}
                  className={cn(
                    "relative flex h-9 items-center justify-center rounded-md border text-[12px] font-medium transition-all",
                    active
                      ? "border-primary bg-primary text-primary-foreground shadow"
                      : "border-lt-border bg-lt-bg2 hover:border-primary/50 hover:bg-primary/5",
                    isFixed && active && "ring-2 ring-lt-teal ring-offset-1 ring-offset-background",
                  )}
                >
                  {String(n).padStart(2, "0")}
                  {isFixed && active && (
                    <Pin className="absolute -right-1 -top-1 h-3 w-3 fill-lt-teal text-lt-teal" />
                  )}
                </button>
              );
            })}
          </div>
          {mode === "fixed" && selectedNumbers.length > 0 && (
            <div className="mt-4 rounded-lg border border-lt-teal/40 bg-lt-teal/5 p-3">
              <div className="mb-2 flex items-center gap-2 text-[12px] font-semibold text-lt-teal">
                <Pin className="h-3.5 w-3.5 fill-lt-teal" />
                Selecione as dezenas que devem ficar FIXAS em todos os jogos:
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {selectedNumbers.map((n) => {
                const f = fixedNumbers.includes(n);
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => toggleFixed(n)}
                    className={cn(
                      "flex h-8 items-center gap-1 rounded-full border-2 px-3 text-[12px] font-medium transition-all",
                      f
                        ? "border-lt-teal bg-lt-teal text-background shadow"
                        : "border-dashed border-lt-teal/50 bg-lt-bg2 text-foreground hover:border-lt-teal hover:bg-lt-teal/10",
                    )}
                  >
                    <Pin className={cn("h-3 w-3", f ? "fill-background" : "fill-lt-teal/60 text-lt-teal/60")} />
                    {String(n).padStart(2, "0")}
                  </button>
                );
                })}
              </div>
            </div>
          )}
          {mode === "pairs" && selectedNumbers.length > 0 && (
            <div className="mt-4">
              <PairBuilder
                selectedNumbers={selectedNumbers}
                value={customPairs}
                onChange={setCustomPairs}
                hint={pairsHint}
              />
            </div>
          )}
        </CardContent>
      </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Estratégia de geração</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <RadioGroup value={mode} onValueChange={(v) => setMode(v as Mode)} className="grid gap-3 md:grid-cols-2">
            <Label className={cn(
              "cursor-pointer rounded-lg border p-4 transition-all md:col-span-2",
              mode === "manual" ? "border-primary bg-primary/5" : "border-lt-border"
            )}>
              <div className="flex items-start gap-3">
                <RadioGroupItem value="manual" id="m-manual" className="mt-1" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 font-medium">
                    <Pin className="h-4 w-4 text-lt-teal" /> Montar meu jogo
                    <span className="rounded-full bg-lt-teal/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-lt-teal">
                      Livre
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Sem combinações nem geração automática: escolha as dezenas na grade
                    ({limits.baseNumbers} a {limits.maxPickPerGame}) e clique em
                    <strong> Adicionar ao bolão</strong>. Você pode misturar com combinações
                    e com o gerador inteligente na mesma lista.
                  </div>
                </div>
              </div>
            </Label>
            <Label className={cn(
              "cursor-pointer rounded-lg border p-4 transition-all md:col-span-2",
              mode === "smart" ? "border-primary bg-primary/5" : "border-lt-border"
            )}>
              <div className="flex items-start gap-3">
                <RadioGroupItem value="smart" id="m-smart" className="mt-1" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 font-medium">
                    <Wand2 className="h-4 w-4 text-primary" /> Gerador inteligente
                    <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
                      Auto
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Você não precisa escolher dezenas — informe quantos jogos quer e o sistema gera
                    com base no histórico (frequência, atrasos, equilíbrio par/ímpar, etc).
                  </div>
                </div>
              </div>
            </Label>
            <Label className={cn(
              "cursor-pointer rounded-lg border p-4 transition-all",
              mode === "complete" ? "border-primary bg-primary/5" : "border-lt-border"
            )}>
              <div className="flex items-start gap-3">
                <RadioGroupItem value="complete" id="m-complete" className="mt-1" />
                <div>
                  <div className="font-medium">Combinações completas</div>
                  <div className="text-xs text-muted-foreground">Todas as combinações C(n, k). Cobertura total, mas mais jogos.</div>
                </div>
              </div>
            </Label>
            <Label className={cn(
              "rounded-lg border p-4 transition-all",
              !closureAllowed && "cursor-not-allowed opacity-60",
              closureAllowed && "cursor-pointer",
              mode === "closure" ? "border-primary bg-primary/5" : "border-lt-border"
            )}>
              <div className="flex items-start gap-3">
                <RadioGroupItem value="closure" id="m-closure" className="mt-1" disabled={!closureAllowed} />
                <div className="flex-1">
                  <div className="flex items-center gap-2 font-medium">
                    Fechamento estratégico
                    {!closureAllowed && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[9px] font-medium uppercase tracking-wider text-primary">
                        <Sparkles className="h-2.5 w-2.5" /> Pro
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Menos jogos, buscando uma meta mínima de acertos se Y forem sorteados.
                    {!closureAllowed && (
                      <>
                        {" "}
                        <Link to="/planos" className="underline hover:text-primary">
                          Disponível no Pro+
                        </Link>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Label>
            <Label className={cn(
              "cursor-pointer rounded-lg border p-4 transition-all",
              mode === "pairs" ? "border-primary bg-primary/5" : "border-lt-border"
            )}>
              <div className="flex items-start gap-3">
                <RadioGroupItem value="pairs" id="m-pairs" className="mt-1" />
                <div>
                  <div className="font-medium">Combinação por duplas</div>
                  <div className="text-xs text-muted-foreground">Suas dezenas viram duplas. Em quantidade ímpar, a dezena que sobra é a âncora e entra em todos os jogos.</div>
                </div>
              </div>
            </Label>
            <Label className={cn(
              "cursor-pointer rounded-lg border p-4 transition-all",
              mode === "fixed" ? "border-primary bg-primary/5" : "border-lt-border"
            )}>
              <div className="flex items-start gap-3">
                <RadioGroupItem value="fixed" id="m-fixed" className="mt-1" />
                <div>
                  <div className="font-medium">Dezenas fixas + variáveis</div>
                  <div className="text-xs text-muted-foreground">Marque algumas dezenas como fixas (aparecem em todos os jogos). As demais combinam livremente para preencher cada jogo.</div>
                </div>
              </div>
            </Label>
          </RadioGroup>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Dezenas por jogo</Label>
              <Select
                value={String(k)}
                onValueChange={(v) => setK(Number(v))}
                disabled={mode === "closure" || mode === "manual"}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                   {Array.from({ length: limits.maxPickPerGame - limits.baseNumbers + 1 }, (_, i) => limits.baseNumbers + i).map((v) => (
                    <SelectItem
                      key={v}
                      value={String(v)}
                      disabled={
                        (mode !== "smart" && mode !== "ai" && v > selectedNumbers.length) ||
                        (mode === "pairs" && selectedNumbers.length > 0 && v % 2 !== selectedNumbers.length % 2) ||
                        (mode === "fixed" && v <= fixedNumbers.length)
                      }
                    >
                      {v} dezenas — {formatBRL(ticketPrice(slug, v))}/jogo
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {mode === "closure" && (
                <p className="text-[11px] text-muted-foreground">
                  Fixo em {strategic.k} dezenas/jogo (aposta mínima da Caixa).
                </p>
              )}
              {mode === "manual" && (
                <p className="text-[11px] text-muted-foreground">
                  No modo livre, o tamanho do jogo é o número de dezenas que você marcar na grade
                  ({selectedNumbers.length} marcada(s) — {formatBRL(ticketPrice(slug, Math.max(limits.baseNumbers, selectedNumbers.length)))}/jogo).
                </p>
              )}
            </div>

            {mode === "smart" && (
              <div className="space-y-2">
                <Label>Quantidade de jogos</Label>
                <Input
                  type="number"
                  min={1}
                  max={1000}
                  value={smartQty}
                  onChange={(e) => setSmartQty(Math.max(1, Math.min(1000, Number(e.target.value))))}
                />
                <p className="text-[10px] text-muted-foreground">de 1 a 1000 jogos</p>
              </div>
            )}

            {mode === "closure" && (
              <div className="space-y-2 md:col-span-2">
                <Label>Buscar ao menos</Label>
                <Select
                  value={String(guaranteeHits)}
                  onValueChange={(v) => setGuaranteeHits(Number(v))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {strategic.tOptions.map((v) => (
                      <SelectItem key={v} value={String(v)}>{v} acertos</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">
                  Para {config.name}: {strategicTHint(slug)} acertos. Dezenas por jogo fixas em {strategic.k}.
                </p>
              </div>
            )}
          </div>

          {mode === "closure" && (
            <div
              className={cn(
                "rounded-lg border p-3 text-[12px]",
                strategicVOk
                  ? "border-lt-teal/40 bg-lt-teal/5 text-foreground"
                  : "border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-300",
              )}
            >
              <strong>Fechamento Estratégico — {config.name}:</strong>{" "}
              escolha exatamente {strategicVHint(slug)} dezenas. Você selecionou{" "}
              <strong>{selectedNumbers.length}</strong>.
            </div>
          )}

          {mode === "smart" && (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm font-medium text-primary">
                  <Wand2 className="h-4 w-4" /> Filtros inteligentes (opcionais)
                </div>
                {(() => {
                  const allOn = SMART_FILTER_KEYS.every((k) => smartFilters[k] === true);
                  return (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setSmartFilters((s) => {
                          const next = { ...s };
                          SMART_FILTER_KEYS.forEach((k) => {
                            (next as any)[k] = !allOn;
                          });
                          return next;
                        })
                      }
                      className="h-7 text-[11px]"
                    >
                      {allOn ? "Desmarcar todos" : "Selecionar todos"}
                    </Button>
                  );
                })()}
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {[
                  { key: "parityBalance" as const, label: "Equilíbrio pares/ímpares" },
                  { key: "sumInRange" as const, label: "Soma na faixa típica" },
                  { key: "decadeSpread" as const, label: "Espalhar por décadas" },
                  { key: "noLongRuns" as const, label: "Sem sequências longas" },
                  { key: "preferHot" as const, label: "Priorizar dezenas quentes" },
                  { key: "includeOverdue" as const, label: "Incluir dezenas atrasadas" },
                  { key: "avoidPastDraws" as const, label: "Evitar jogos já sorteados" },
                ].map((f) => (
                  <label
                    key={f.key}
                    className={cn(
                      "flex cursor-pointer items-center gap-2 rounded-md border p-2 text-[13px] transition-colors",
                      smartFilters[f.key]
                        ? "border-primary/50 bg-primary/10"
                        : "border-lt-border bg-lt-bg2 hover:border-primary/30",
                    )}
                  >
                    <Checkbox
                      checked={smartFilters[f.key] === true}
                      onCheckedChange={(v) =>
                        setSmartFilters((s) => ({ ...s, [f.key]: v === true }))
                      }
                    />
                    {f.label}
                  </label>
                ))}
              </div>
              {!smartData && (
                <p className="mt-3 text-[11px] text-muted-foreground">
                  Carregando histórico estatístico…
                </p>
              )}
            </div>
          )}

          {preview && (
            <div className="rounded-lg border border-lt-gold-border bg-lt-gold-bg p-4">
              <div className="flex items-center justify-between gap-2 text-sm font-medium text-primary">
                <span className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4" /> Previsão
                </span>
                {strategicInfo && (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span
                      title={
                        strategicInfo.badge === "exato"
                          ? `Resultado exato — bate a referência publicada (${strategicInfo.target} jogos).`
                          : strategicInfo.badge === "melhor"
                          ? `Melhor que a referência: gerou ${strategicInfo.actual} jogos vs. ${strategicInfo.target} da literatura.`
                          : `Aproximação — referência publicada: ${strategicInfo.target}; gerador entregou ${strategicInfo.actual}.`
                      }
                      className={
                        "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider " +
                        (strategicInfo.badge === "aproximacao"
                          ? "bg-amber-500/20 text-amber-700 dark:text-amber-300"
                          : "bg-lt-teal/20 text-lt-teal")
                      }
                    >
                      {strategicInfo.badge === "exato"
                        ? `Exato · ref. ${strategicInfo.target}`
                        : strategicInfo.badge === "melhor"
                        ? `Otimizado · ref. ${strategicInfo.target}`
                        : `Aprox · ref. ${strategicInfo.target}`}
                    </span>
                    {strategicInfo.badge === "melhor" && strategicInfo.coverageVerified && (
                      <span
                        title={`Verificamos todos os ${strategicInfo.coverageTested?.toLocaleString("pt-BR")} subconjuntos: cada um aparece em pelo menos um jogo. Garantia matematicamente comprovada.`}
                        className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300"
                      >
                        ✓ Cobertura verificada
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="mt-2 grid gap-3 text-[13px] sm:grid-cols-2">
                <div>
                  <div className="text-muted-foreground">
                    {preview.isStrategic
                      ? "Jogos (referência publicada)"
                      : preview.isUpper
                      ? "Jogos (limite superior)"
                      : "Jogos gerados"}
                  </div>
                  <div className="text-lg font-medium">{formatInt(preview.total)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Custo total estimado</div>
                  <div className="text-lg font-medium">{formatBRL(preview.cost)}</div>
                </div>
              </div>
              {strategicInfo && (
                <div className="mt-3 rounded-md border border-lt-border bg-background/40 p-2.5 text-[11px] leading-relaxed text-muted-foreground">
                  {strategicInfo.badge === "exato" && (
                    <p>
                      <strong className="text-foreground">Resultado exato</strong> —
                      iguala a referência publicada em sistemas de fechamento
                      ({strategicInfo.target} jogos).
                    </p>
                  )}
                  {strategicInfo.badge === "melhor" && (
                    <>
                      <p>
                        <strong className="text-foreground">Otimizado:</strong>{" "}
                        gerador encontrou <strong>{strategicInfo.actual}</strong>{" "}
                        jogos —{" "}
                        <strong>
                          {strategicInfo.target - strategicInfo.actual}
                        </strong>{" "}
                        a menos que a referência ({strategicInfo.target}). A
                        referência é um <em>limite superior conhecido</em>, não o
                        mínimo absoluto: para muitos C(v, k, t) o ótimo continua
                        em aberto.
                      </p>
                      {strategicInfo.coverageVerified && (
                        <p className="mt-1.5">
                          <strong className="text-emerald-700 dark:text-emerald-300">
                            ✓ Cobertura verificada
                          </strong>{" "}
                          — testamos todos os{" "}
                          {strategicInfo.coverageTested?.toLocaleString("pt-BR")}{" "}
                          subconjuntos de {guaranteeHits} dezenas: cada um está em
                          pelo menos um jogo. Garantia comprovada.
                        </p>
                      )}
                    </>
                  )}
                  {strategicInfo.badge === "aproximacao" && (
                    <p>
                      <strong className="text-foreground">Aproximação:</strong>{" "}
                      gerador entregou <strong>{strategicInfo.actual}</strong>{" "}
                      jogos, acima da referência ({strategicInfo.target}). A
                      garantia se mantém, com alguns jogos extras.
                    </p>
                  )}
                  <p className="mt-1.5 opacity-80">
                    <strong>Referências:</strong> La Jolla Covering Repository
                    (LJCR); Covering Repository; D. Bluskov,{" "}
                    <em>Combinatorial Designs for Lotteries</em>; tabelas brasileiras
                    consagradas.
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="rounded-lg border border-lt-teal/40 bg-lt-teal/5 p-3 text-[13px]">
            <div className="flex items-start gap-2">
              <Users className="mt-0.5 h-4 w-4 text-lt-teal" />
              <div>
                <strong className="text-foreground">Próximo passo:</strong>{" "}
                <span className="text-muted-foreground">
                  Após gerar o bolão, você poderá adicionar os <strong>participantes (cotistas)</strong> e
                  o sistema calculará automaticamente o valor que cada um paga.
                </span>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => navigate("/app/boloes")}>Cancelar</Button>
            <Button onClick={handleAddToBasket} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Adicionar ao bolão
            </Button>
          </div>
        </CardContent>
      </Card>

      <PoolGameBasket
        slug={slug}
        games={basket.games}
        onRemove={basket.removeAt}
        onClear={basket.clear}
        footer={
          <Button onClick={handleCreate} disabled={saving || basket.games.length === 0}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Criar bolão
          </Button>
        }
      />
    </div>
  );
}
